import { FormEvent, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, CheckCircle2, Loader2 } from "lucide-react";

type QuoteModalProps = { open: boolean; onClose: () => void };

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function QuoteModal({ open, onClose }: QuoteModalProps) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setSent(false);
      setError("");
    }
  }, [open]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(e.currentTarget);
    const firstName = String(form.get("first_name") || "").trim();
    const email = String(form.get("email") || "").trim();
    const company = String(form.get("company") || "").trim();
    const message = String(form.get("message") || "").trim();
    if (!firstName) {
      setError("Please enter your first name.");
      setBusy(false);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      setError("Please enter a valid work email address.");
      setBusy(false);
      return;
    }
    if (!company) {
      setError("Please enter your company name.");
      setBusy(false);
      return;
    }
    if (!message) {
      setError("Please tell us briefly about your project.");
      setBusy(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(Object.fromEntries(form)),
      });
      if (!res.ok) throw new Error("Request failed");
      setSent(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  const inputClass =
    "w-full rounded-xl border border-brand-mist bg-brand-mist/60 px-4 py-3 text-sm text-brand-teal-dark placeholder:text-brand-slate/50 outline-none transition-colors duration-300 focus:border-brand-orange focus:bg-white";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-brand-teal-dark/70 p-4 backdrop-blur-md"
          onClick={onClose}
          data-testid="quote-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 44, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-lg overflow-hidden rounded-[24px] bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between bg-brand-teal px-8 py-7">
              <div>
                <h3 className="font-display text-2xl font-extrabold text-white">Get a custom quote</h3>
                <p className="mt-1 text-sm text-white/75">
                  Tell us about your project and we&rsquo;ll reply within one business day.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                data-testid="quote-modal-close"
                className="text-white/70 transition-colors duration-300 hover:text-white"
              >
                <X size={22} />
              </button>
            </div>

            {sent ? (
              <div className="flex flex-col items-center gap-4 px-8 py-14 text-center" data-testid="quote-success">
                <CheckCircle2 size={52} className="text-brand-orange" strokeWidth={1.4} />
                <p className="font-display text-xl font-bold text-brand-teal">Thanks. Request received.</p>
                <p className="max-w-xs text-sm text-brand-slate">
                  A senior specialist will review your project and get back to you within one business day.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="grid grid-cols-1 gap-4 px-8 py-8 sm:grid-cols-2" data-testid="quote-form">
                <input name="first_name" placeholder="First name *" className={inputClass} data-testid="quote-first-name" maxLength={80} />
                <input name="last_name" placeholder="Last name (optional)" className={inputClass} data-testid="quote-last-name" maxLength={80} />
                <input name="email" type="email" placeholder="Work email *" className={`${inputClass} sm:col-span-2`} data-testid="quote-email" />
                <input name="company" placeholder="Company *" className={inputClass} data-testid="quote-company" maxLength={120} />
                <input name="phone" placeholder="Phone (optional)" className={inputClass} data-testid="quote-phone" maxLength={30} />
                <textarea
                  name="message"
                  rows={3}
                  placeholder="What roles are you hiring? Project, seniority, timeline… *"
                  className={`${inputClass} resize-none sm:col-span-2`}
                  data-testid="quote-message"
                  maxLength={2000}
                />
                {/* Honeypot: invisible to humans */}
                <input
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="pointer-events-none absolute -left-[9999px] h-0 w-0 opacity-0"
                />
                {error && (
                  <p className="text-sm font-medium text-red-600 sm:col-span-2" data-testid="quote-error">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="quote-form-submit-button"
                  className="flex items-center justify-center gap-2 rounded-xl bg-brand-orange px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-brand-orange/30 transition-colors duration-300 hover:bg-brand-orange-dark disabled:opacity-60 sm:col-span-2"
                >
                  {busy && <Loader2 size={16} className="animate-spin" />}
                  {busy ? "Sending…" : "Request my quote"}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
